// Function index:
//   createTokenRegistry(tokenLayer) — owns the character + NPC token maps and
//       their add/remove/move lifecycle over the token layer. Placement is
//       collision-aware across every token. moveCharacter reports whether the
//       moved token is the player's own, so the scene can refresh fog /
//       overlays / streaming on a self-move.
//   makeTokenContainer — re-exported from tokens.ts so both FlatRenderer (via
//       this registry) and IsoRenderer build tokens from the same visuals.

import type { Container, Ticker } from 'pixi.js';
import { makeNpcToken, makeToken, placeTokenAt } from './tokens.js';
import type { CharacterInput, NpcInput, TokenHandle } from './types.js';

export { makeTokenContainer } from './tokens.js';

export interface TokenRegistry {
  addCharacter(c: CharacterInput): void;
  removeCharacter(id: number): void;
  // Returns { isSelf } for the moved token, or null when no such token exists.
  moveCharacter(id: number, q: number, r: number, ticker: Ticker): { isSelf: boolean } | null;
  addNpc(n: NpcInput): void;
  removeNpc(id: number): void;
  moveNpc(id: number, q: number, r: number, ticker: Ticker): void;
}

export function createTokenRegistry(tokenLayer: Container): TokenRegistry {
  const tokens = new Map<number, TokenHandle>();
  const npcTokens = new Map<number, TokenHandle>();

  // Every token (characters + NPCs) — placeTokenAt uses this to nudge tokens
  // sharing a hex so they don't fully overlap.
  const all = (): Iterable<TokenHandle> => (function* () {
    for (const t of tokens.values()) yield t;
    for (const t of npcTokens.values()) yield t;
  })();

  const destroy = (h: TokenHandle): void => {
    tokenLayer.removeChild(h.container);
    h.container.destroy({ children: true });
  };

  return {
    addCharacter(c) {
      if (tokens.has(c.id)) return;
      const h = makeToken(c, tokenLayer);
      tokens.set(c.id, h);
      placeTokenAt(h, c.q, c.r, all());
    },
    removeCharacter(id) {
      const h = tokens.get(id);
      if (!h) return;
      destroy(h);
      tokens.delete(id);
    },
    moveCharacter(id, q, r, ticker) {
      const h = tokens.get(id);
      if (!h) return null;
      placeTokenAt(h, q, r, all(), { ticker });
      return { isSelf: h.isSelf };
    },
    addNpc(n) {
      if (npcTokens.has(n.id)) return;
      const h = makeNpcToken(n, tokenLayer);
      npcTokens.set(n.id, h);
      placeTokenAt(h, n.q, n.r, all());
    },
    removeNpc(id) {
      const h = npcTokens.get(id);
      if (!h) return;
      destroy(h);
      npcTokens.delete(id);
    },
    moveNpc(id, q, r, ticker) {
      const h = npcTokens.get(id);
      if (!h) return;
      placeTokenAt(h, q, r, all(), { ticker });
    },
  };
}
